import React, { useEffect, useState, Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useStore } from '../lib/store';
import { haptics } from '../lib/utils/haptics';
import { toast } from 'sonner';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { CategoryNav } from '../components/layout/CategoryNav';
import { ProductImageGallery } from '../components/product/ProductImageGallery';
import { ProductInfo } from '../components/product/ProductInfo';
import { BundleDeals } from '../components/product/BundleDeals';
import { AddToCart } from '../components/product/AddToCart';
import { DeliveryInfo } from '../components/product/DeliveryInfo';
import { ProductBenefits } from '../components/product/ProductBenefits';
import { FrequentlyBoughtTogether } from '../components/product/FrequentlyBoughtTogether';
import { ProductTabs } from '../components/product/ProductTabs';
import { CustomersAlsoViewed } from '../components/product/CustomersAlsoViewed';
import { RelatedProducts } from '../components/product/RelatedProducts';
import { PrescriptionRequired } from '../components/product/PrescriptionRequired';
import { PharmacistConsultation } from '../components/product/PharmacistConsultation';
import { StickyBottomActions } from '../components/product/StickyBottomActions';
import { SEO } from '../components/shared/SEO';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { HomeIcon, ChevronRightIcon } from 'lucide-react';
import { ComboComponents } from '../components/product/ComboComponents';
import { PaymentOptions } from '../components/product/PaymentOptions';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { PRODUCT_IMAGES } from '../data/productImages';
export function ProductDetailPage() {
  const {
    productId
  } = useParams();
  const navigate = useNavigate();
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  // Mock product data
  const product = {
    id: productId || '1',
    name: 'COSRX Advanced Snail 96 Mucin Power Essence 100ml',
    brand: 'COSRX',
    sku: '135556',
    price: 99.25,
    originalPrice: 106,
    discount: 15,
    rating: 4.8,
    reviewCount: 324,
    inStock: true,
    stockStatus: 'In Stock',
    isGlobal: true,
    images: [PRODUCT_IMAGES[0], PRODUCT_IMAGES[1], PRODUCT_IMAGES[2], PRODUCT_IMAGES[3], PRODUCT_IMAGES[4], PRODUCT_IMAGES[0]],
    tags: ['Dry Skin', 'Dark Circles'],
    prescriptionNeeded: false,
    description: 'COSRX Advanced Snail 96 Mucin Power Essence is a lightweight essence that nourishes, repairs, and revitalizes the skin. Formulated with 96.3% Snail Secretion Filtrate, it helps to improve skin elasticity, soothe redness, and provide deep hydration for a healthy, glowing complexion.',
    loyaltyPoints: 99
  };
  const breadcrumbItems = [{
    label: 'Home',
    href: '/'
  }, {
    label: 'Skincare',
    href: '/category/skin-care'
  }, {
    label: 'Essences & Serums',
    href: '/category/essences-serums'
  }, {
    label: product.name
  }];
  // Generate product URL key from product name
  const productUrlKey = product.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  // Generate keywords from product data
  const productKeywords = [product.brand, product.name, ...product.tags, 'Saudi Arabia', 'online pharmacy', 'authentic products', 'fast delivery'].join(', ');
  // SEO title and description
  const seoTitle = `${product.brand} ${product.name} Price ${product.price} SAR`;
  const seoDescription = `Buy ${product.name} by ${product.brand} at Innova Pharmacy — authentic product with fast delivery across Saudi Arabia and secure checkout in SAR.`;
  return <HelmetProvider data-id="element-7552" data-mp="13mx1v10frnu4">
      <SEO title={seoTitle} description={seoDescription} canonical={`/en/${productUrlKey}/`} ogType="product" ogImage={product.images[0]} ogPrice={product.price} productPrice={product.price} productCurrency="SAR" robots="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" twitterCard="summary_large_image" hreflangAr={`/${productUrlKey}/`} hreflangEn={`/en/${productUrlKey}/`} hreflangDefault={`/${productUrlKey}/`} author={product.brand} keywords={productKeywords} data-id="element-7553" data-mp="1rf3upq6ydcos" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-7554" data-mp="3ga5a1e1a7y5">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-7555" data-mp="3unw51ixmtq9q">
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} disableShrink={false} showAnnouncement={true} data-id="element-7556" data-mp="3un4iexm0j2vl" />
        </div>
        <main className="flex-grow pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)] pb-32 lg:pb-0" data-id="element-7557" data-mp="3gtmwj65ute2e">
          {/* Mobile Header Overlay */}
          <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-7558" data-mp="3qmkkvsxj1x8x">
            <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} alwaysWhiteBackground={false} data-id="element-7559" data-mp="3luj4butbtkd3" />
          </div>

          {/* Page Hero Breadcrumb - Path Only Mode */}
          <PageHeroBreadcrumb breadcrumbs={breadcrumbItems} title={product.name} description={product.description} showTitle={false} showDescription={false} data-id="element-7560" data-mp="3iuiyc3cl77pz" />
          {/* Product Main Section */}
          <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 pb-8 lg:pb-6" data-id="element-7561" data-mp="3rnmdqh6slun9">
            {/* Mobile Layout - Reordered */}
            <div className="lg:hidden space-y-4" data-id="element-7562" data-mp="2zwgbnv7ihmvc">
              {/* Product Image Gallery */}
              <div data-id="element-7563" data-mp="33wqh96re6b9o">
                <ProductImageGallery images={product.images} productName={product.name} discount={product.discount} prescriptionNeeded={product.prescriptionNeeded} data-id="element-7564" data-mp="33ybej453c8b8" />
              </div>
              {/* Product Info - without ComboComponents and PaymentOptions */}
              <div data-id="element-7565" data-mp="t58i7998r1he">
                <ProductInfo product={product} hideComboComponents={true} hidePaymentOptions={true} data-id="element-7566" data-mp="t4p98i1kjnfi" />
              </div>
              {/* Bundle & Save */}
              <div data-id="element-7567" data-mp="28phiagxb8s7m">
                <BundleDeals product={product} data-id="element-7568" data-mp="28oy9bppn1e5q" />
              </div>
              {/* Frequently Bought Together */}
              <div data-id="element-7569" data-mp="23v3hu03sjuw3">
                <FrequentlyBoughtTogether data-id="element-7570" data-mp="1yymsx3orpchy" />
              </div>
              {/* Combo Components */}
              <div data-id="element-7571" data-mp="1uwi0g8a22lww">
                <ComboComponents data-id="element-7572" data-mp="1uxt6yfuvy7rp" />
              </div>
              {/* Payment Options */}
              <div data-id="element-7573" data-mp="1pxf89of6ca6c">
                <PaymentOptions price={product.price} data-id="element-7574" data-mp="1pwvrib19frtw" />
              </div>
              {/* Prescription Required */}
              <div data-id="element-7575" data-mp="1kyu6vlo6rkgb">
                <PrescriptionRequired data-id="element-7576" data-mp="1l0f11a6l91dn" />
              </div>
              {/* Pharmacist Consultation */}
              <div data-id="element-7577" data-mp="3fcmqw941ys2k">
                <PharmacistConsultation data-id="element-7578" data-mp="3fd4vbt0vcclk" />
              </div>
              {/* Delivery Information */}
              <div data-id="element-7579" data-mp="3ada6dv0nlx35">
                <DeliveryInfo data-id="element-7580" data-mp="3aeto7q1ykg71" />
              </div>
            </div>
            {/* Desktop Layout */}
            <div className="hidden lg:grid lg:grid-cols-[400px_1fr_400px] xl:grid-cols-[420px_1fr_420px] 2xl:grid-cols-[440px_1fr_440px] gap-4 xl:gap-6" data-id="element-7581" data-mp="2j9h86tj3rww9">
              {/* Left Column - Gallery (Sticky) */}
              <div className="order-1" data-id="element-7582" data-mp="2afcmeoittbnl">
                <div className="sticky top-24" data-id="element-7583" data-mp="2ehfztip22qbi">
                  <ProductImageGallery images={product.images} productName={product.name} discount={product.discount} prescriptionNeeded={product.prescriptionNeeded} data-id="element-7584" data-mp="2eg4vnhrp7gjv" />
                </div>
              </div>
              {/* Middle Column - Product Info + Frequently Bought Together */}
              <div className="order-2 space-y-6" data-id="element-7585" data-mp="1vjmvqd2p4acj">
                <div className="sticky top-24" data-id="element-7586" data-mp="1vkerpp07ga3c">
                  <ProductInfo product={product} data-id="element-7587" data-mp="3lw2njt8e7dac" />
                </div>
                <div data-id="element-7588" data-mp="3pwcv3abu2brb">
                  <FrequentlyBoughtTogether data-id="element-7589" data-mp="3pvl64icqrca0" />
                </div>
              </div>
              {/* Right Column - Sidebar (Sticky) */}
              <div className="order-3" data-id="element-7590" data-mp="1e76mtfm4rpvb">
                <div className="sticky top-24 space-y-4 xl:space-y-6" data-id="element-7591" data-mp="1a87hhtwhlr3c">
                  <BundleDeals product={product} data-id="element-7592" data-mp="1a5uuv3fsasjy" />
                  <AddToCart product={product} data-id="element-7593" data-mp="1nzuha3pn1hqv" />
                  <PrescriptionRequired data-id="element-7594" data-mp="1j8cfxejpdf5d" />
                  <PharmacistConsultation data-id="element-7595" data-mp="1j7b252bayejy" />
                  <DeliveryInfo data-id="element-7596" data-mp="1j69iwak4ulr5" />
                </div>
              </div>
            </div>
            {/* Product Tabs */}
            <div className="mt-6 lg:mt-8" data-id="element-7597" data-mp="1syfgq0x2fxe9">
              <ProductTabs product={product} data-id="element-7598" data-mp="1sxdwp6y2o0ke" />
            </div>
            {/* Customers Also Viewed */}
            <div className="mt-6 lg:mt-8" data-id="element-7599" data-mp="26xewsk6ij3yt">
              <CustomersAlsoViewed data-id="element-7600" data-mp="21z3k81ou2fcw" />
            </div>
            {/* Related Products */}
            <div className="mt-6 lg:mt-8 mb-6 lg:mb-0" data-id="element-7601" data-mp="cjxg9h4iz7da">
              <RelatedProducts data-id="element-7602" data-mp="ckfkp11ccrwa" />
            </div>
          </div>
        </main>
        <div className="hidden md:block" data-id="element-7603" data-mp="7lvxyyexbc2v">
          <Footer data-id="element-7604" data-mp="7mxbd9jewanr" />
        </div>
        <MobileFooterNav data-id="element-7605" data-mp="vhyarozm69vn" />
        <PharmacistChat data-id="element-7606" data-mp="vj9e5np5dfm8" />
        {/* Sticky Bottom Actions - Mobile Only */}
        <StickyBottomActions product={product} data-id="element-7607" data-mp="qilr1d41hixa" />
        {/* Back to top button */}
        <button className="fixed bottom-44 md:bottom-6 right-4 md:right-6 bg-[#1E2B55] text-white p-3 rounded-full shadow-lg hover:bg-[#162041] transition-colors z-40" onClick={() => window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })} data-id="element-7608" data-mp="mh8l730y5fqd">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-7609" data-mp="1a6uljtt220zy">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" data-id="element-7610" data-mp="10i2urozqdmqm" />
          </svg>
        </button>
        {/* Location Modal */}
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-7611" data-mp="1t4dxp6at7zql" />
      </div>
    </HelmetProvider>;
}